import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FileUpload,
  Header,
  ProgressBar,
  SpaceBetween,
} from '@cloudscape-design/components';
import { useNotifications } from '@features/notifications';

import { useUploadCampaignCsv } from '../hooks/use-upload-campaign-csv';
import { MAX_CSV_BYTES, UploadTarget, validateCsvFile } from '../services/campaign-uploads';

interface CampaignCsvUploadProps {
  campaignId: string;
  campaignName: string;
  /** Destination folder for this campaign's uploads, forwarded to the presign request. */
  target?: UploadTarget;
  /** When true the campaign is inactive, so uploading is not offered at all. */
  disabled?: boolean;
}

const LIMIT_MB = Math.floor(MAX_CSV_BYTES / (1024 * 1024));

/**
 * CSV upload panel for a single campaign: pick one .csv file, then upload it to
 * S3 on an explicit button click. Validation runs before any network call.
 */
export function CampaignCsvUpload({
  campaignId,
  campaignName,
  target,
  disabled = false,
}: CampaignCsvUploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { addNotification } = useNotifications();
  const { mutateAsync, isPending, progress } = useUploadCampaignCsv();

  const selectedFile = files[0] ?? null;

  const handleFilesChange = (nextFiles: File[]) => {
    setFiles(nextFiles);
    const file = nextFiles[0];
    setValidationError(file ? validateCsvFile(file) : null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const problem = validateCsvFile(selectedFile);
    if (problem) {
      setValidationError(problem);
      return;
    }

    try {
      await mutateAsync({ campaignId, file: selectedFile, target });
      addNotification('success', `${selectedFile.name} uploaded to ${campaignName}`);
      setFiles([]);
    } catch (error) {
      // Dynamic import matches the existing pattern in BulkUploadDialog and keeps
      // error-messages in its own chunk.
      const { translateError } = await import('@features/notifications/error-messages');
      const translated = translateError(error);
      addNotification('error', translated.message, translated.technicalDetails);
    }
  };

  const canUpload = selectedFile !== null && validationError === null && !isPending;

  // Cloudscape FileUpload has no disabled prop, so the picker and button are
  // withheld entirely rather than left inert for an inactive campaign.
  if (disabled) {
    return (
      <Container header={<Header variant="h2">Upload CSV</Header>}>
        <Alert type="info">
          This campaign is inactive. Set its status to Active to upload contact lists.
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      header={
        <Header variant="h2" description="Upload a CSV contact list for this campaign.">
          Upload CSV
        </Header>
      }
    >
      <SpaceBetween size="m">
        {validationError && (
          <Alert type="error" dismissible onDismiss={() => setValidationError(null)}>
            {validationError}
          </Alert>
        )}

        <FileUpload
          value={files}
          onChange={({ detail }) => handleFilesChange(detail.value)}
          accept=".csv"
          showFileSize
          showFileLastModified
          tokenLimit={1}
          constraintText={`One .csv file, up to ${LIMIT_MB} MB.`}
          i18nStrings={{
            uploadButtonText: () => 'Choose CSV file',
            dropzoneText: () => 'Drop a .csv file here',
            removeFileAriaLabel: (fileIndex) => `Remove file ${fileIndex + 1}`,
            limitShowFewer: 'Show fewer files',
            limitShowMore: 'Show more files',
            errorIconAriaLabel: 'Error',
          }}
        />

        {isPending && (
          <ProgressBar
            value={progress}
            label="Uploading"
            description={selectedFile?.name}
            additionalInfo={`${progress}% complete`}
          />
        )}

        <Box>
          <Button variant="primary" disabled={!canUpload} loading={isPending} onClick={handleUpload}>
            Upload CSV
          </Button>
        </Box>
      </SpaceBetween>
    </Container>
  );
}
